import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { FileApi } from '../apis/file-api';
import { SiaFileArchive } from '../interfaces/sia-file-archive';
import { SiaArchivePath } from '../interfaces/sia-file-path';
import { SiaHttpService } from './http.service';
import { notify } from './notify';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private httpService = inject(SiaHttpService);
  private api = inject(FileApi);

  private sanitizer = inject(DomSanitizer);

  #menusToIgnore = ['default-login'];

  convertBlob(blob: Blob): SafeUrl | null {
    if (!blob) return null;
    if (blob.size === 0) return null;
    let objectURL = URL.createObjectURL(blob);
    let c = this.sanitizer.bypassSecurityTrustUrl(objectURL);
    return c;
  }

  async downloadPerspective(componentId: string, componentType: string) {
    try {
      const response: any = await this.api.downloadPerspective(
        componentId,
        componentType,
      );
      const blob: Blob = response?.body;
      if (!blob) {
        notify('Impossibile scaricare la prospettiva', 2000, 'error');
        return;
      }

      // Try to parse filename from Content-Disposition header
      let filename = 'perspective.zip';
      const contentDisposition =
        response?.headers?.get && response.headers.get('content-disposition');
      if (contentDisposition) {
        // supports filename*=UTF-8''encoded and filename="name"
        const match =
          /filename\*=UTF-8''(?<fn>[^;]+)|filename=\"?(?<fn2>[^\";]+)\"?/.exec(
            contentDisposition,
          );
        const fn =
          match?.groups?.fn ?? match?.groups?.fn2 ?? match?.[1] ?? match?.[2];
        if (fn) {
          filename = decodeURIComponent(fn);
        }
      } else if (componentId) {
        filename = `${componentId}_${new Date().toISOString().replace(/[:.]/g, '')}.zip`;
      }

      this.triggerDownload(blob, filename, false);
    } catch (e) {
      console.error(e);
      notify('Errore durante il download della prospettiva', 2000, 'error');
    }
  }

  convertBase64ToBlob(base64: string, type: string): Blob {
    // Decode the Base64 string
    const binaryString = atob(base64);

    // Convert the binary string to a Uint8Array
    const uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the Uint8Array
    return new Blob([uint8Array], { type: type });
  }

  async uploadFile(
    archive: SiaArchivePath,
    file: Blob | File,
    returnUrl?: boolean,
    onProgress?: (progress: number) => void,
  ) {
    const fileName =
      archive.newName || (file instanceof File ? file.name : 'file');
    const fileHandle = await this.api.startUpload(archive, fileName);
    if (!fileHandle) {
      console.error('Impossibile caricare il file');
    }

    await this.uploadChunks(fileHandle, file, onProgress);

    return this.api.endUpload(
      archive,
      file.size,
      fileHandle,
      fileName,
      archive.path,
      returnUrl,
    );
  }

  deleteFile(
    filePath: string,
    isRelative: boolean,
    isSystemArchive: boolean,
    archiveId?: string,
  ): Promise<boolean> {
    const archivePath: SiaArchivePath = {
      isRelativePath: isRelative,
      isSystemArchive: isSystemArchive,
      path: filePath,
      id: archiveId,
    };
    return this.api.deleteFile(archivePath);
  }

  async downloadFile(
    filePath: string,
    name: string,
    isRelative: boolean,
    isSystemArchive: boolean,
    archiveId?: string,
  ) {
    try {
      const blob = await this.getFileBlob(
        filePath,
        isRelative,
        isSystemArchive,
        archiveId,
      );
      this.triggerDownload(blob, name, false);
    } catch (e) {
      console.error(e);
    }
  }

  async getFilesNames(
    folderPath: string,
    isRelative: boolean,
  ): Promise<string[]> {
    return this.api.getFilesNames(folderPath, isRelative);
  }

  async moveFileAndDelete(
    move: string,
    to: string,
    deleteFile: string,
    isRelative: boolean,
  ) {
    return this.api.moveFileAndDelete(move, to, deleteFile, isRelative);
  }

  async downloadFilesAsZip(
    folderPath: string,
    zipFileName = 'download.zip',
    isRelative = false,
  ) {
    try {
      const fileNames = await this.getFilesNames(folderPath, isRelative);
      if (!fileNames || fileNames.length === 0) {
        console.error('No files found in the folder');
        return;
      }

      // Ensure the API returns a Blob. If using axios, the API should be set with responseType 'blob'
      const response = await this.api.downloadFilesAsZip(
        folderPath,
        isRelative,
      );

      const zipBlob = this.convertBase64ToBlob(
        response.fileContents,
        'application/zip',
      );

      this.triggerDownload(zipBlob, zipFileName, false);

      notify(
        `{{nt}}${fileNames.length}{{/nt}} file scaricati {{nt}}${zipFileName}{{/nt}}`,
        2000,
        'success',
      );
    } catch (error) {
      console.error(error);
    }
  }

  private _fileUploaded: Subject<string> = new Subject<string>();
  private _fileDeleted: Subject<any> = new Subject<any>();

  getFileNames(folder: string, filterExtensions?: string[]) {
    return this.api.getFileNames(folder, filterExtensions);
  }

  getArchives(key: string): Promise<SiaFileArchive[]> {
    // Some menus (e.g. the "default-login" menu) are intentionally excluded from
    // archive loading for performance/security reasons. The list of excluded
    // menu keys is maintained in the private `#menusToIgnore` field of this
    // service. To change which menus are ignored, update that configuration
    // instead of hardcoding additional checks here.

    if (this.#menusToIgnore.includes(key)) {
      return Promise.resolve([]);
    }
    return this.api.getArchives(key);
  }

  getArchive(id: string): Promise<SiaFileArchive | null> {
    return this.api.getArchive(id);
  }
  pathHasContent(archivePath: SiaArchivePath): Promise<boolean> {
    return this.api.pathHasContent(archivePath);
  }

  getBase64File(file: SiaArchivePath): Promise<string> {
    return this.api.getBase64File(file);
  }

  saveBase64File(file: SiaArchivePath): Promise<boolean> {
    return this.api.saveBase64File(file);
  }

  getFiles(path: SiaArchivePath) {
    return this.api.getFiles(path);
  }
  getSystemFiles(path: SiaArchivePath) {
    return this.api.getSystemFiles(path);
  }
  getSystemArchives(): Promise<SiaFileArchive[]> {
    return this.api.getSystemArchives();
  }
  renameFile(file: SiaArchivePath): Promise<boolean> {
    return this.api.renameFile(file);
  }

  getFileTree(value: SiaArchivePath) {
    return this.api.getFileTree(value);
  }

  createFolder(archive: SiaArchivePath, name: string): Promise<any> {
    return this.api.createFolder(archive, name);
  }

  move(
    name: string,
    moveToFolder: string,
    parent: string,
    archive: SiaArchivePath,
  ): Promise<boolean> {
    return this.api.move(name, moveToFolder, parent, archive);
  }

  moveUp(
    name: string,
    parent: string,
    archive: SiaArchivePath,
  ): Promise<boolean> {
    return this.api.moveUp(name, parent, archive);
  }

  moveAll(
    tmpPath: SiaArchivePath,
    finalPath: SiaArchivePath,
  ): Promise<boolean> {
    return this.api.moveAll(tmpPath, finalPath);
  }

  get fileUploaded$() {
    let r = this._fileUploaded.asObservable();
    return r;
  }

  get fileDeleted$() {
    return this._fileDeleted.asObservable();
  }

  notifyFileUploaded(fileName: string) {
    this._fileUploaded.next(fileName);
  }

  getResourcesUrl(fileName: string) {
    let apiUrl = this.httpService.getApiUrl();
    return `${apiUrl?.replace('api', 'Resources')}/${fileName}`;
  }

  notifyFileDeleted(fileName: string) {
    this._fileDeleted.next(fileName);
  }

  getBlob(filePath: string, isRelative: boolean = true): Promise<Blob> {
    return this.getFileBlob(filePath, isRelative, false);
  }

  private getFileBlob(
    filePath: string,
    isRelative: boolean,
    isSystemArchive: boolean,
    archiveId?: string,
  ): Promise<Blob> {
    const archivePath: SiaArchivePath = {
      isRelativePath: isRelative,
      isSystemArchive: isSystemArchive,
      path: filePath,
      id: archiveId,
    };
    return this.api.downloadFile(archivePath);
  }

  private async uploadChunks(
    fileHandle: string,
    file: Blob,
    onProgress?: (progress: number) => void,
  ) {
    const chunkSize = 320000;
    for (let offset = 0; offset < file.size; offset += chunkSize) {
      const chunk = file.slice(offset, offset + chunkSize);
      await this.api.sendFileChunk(fileHandle, chunk, offset);
      if (onProgress) {
        onProgress(
          Math.min(100, Math.round(((offset + chunk.size) / file.size) * 100)),
        );
      }
    }
  }

  private triggerDownload(
    blob: Blob,
    name: string,
    openPdfInline: boolean,
    wrapAsOctetStream: boolean = false,
  ) {
    // Wrap blob as octet-stream if requested (forces download, prevents inline PDF opening)
    if (wrapAsOctetStream) {
      blob = new Blob([blob], { type: 'application/octet-stream' });
    }

    const fileURL = URL.createObjectURL(blob);
    if (!fileURL) {
      return;
    }

    if (
      openPdfInline &&
      !wrapAsOctetStream &&
      blob.type &&
      blob.type.toString() === 'application/pdf'
    ) {
      window.open(fileURL, '_blank');
      return;
    }

    let a = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.href = fileURL;
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(fileURL);
    a.remove();
  }
}
